import React, { useMemo, useState } from 'react'
import { BackLink, DetailGrid, SummaryGrid, Timeline } from '../components/AdminCommon'
import { Card, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import { moderationCases, MODERATION_TYPE_LABELS } from '../data/moderationData'

const FILTERS = [
  ['ALL', '전체'],
  ['PUBLISHED', '공개'],
  ['HIDDEN', '블라인드'],
  ['DELETED', '삭제됨'],
]

export function ModerationCenter({ navigate, query }) {
  const initialOwner = query?.get('owner') || ''
  const [search, setSearch] = useState(initialOwner)
  const [status, setStatus] = useState('ALL')
  const rows = useMemo(() => moderationCases.filter((item) => {
    const haystack = [item.title, item.owner, item.source, item.targetId].join(' ').toLowerCase()
    const matchesSearch = !search || haystack.includes(search.toLowerCase())
    const matchesStatus = status === 'ALL' || item.moderationStatus === status
    return matchesSearch && matchesStatus
  }), [search, status])

  const hidden = moderationCases.filter((item) => item.moderationStatus === 'HIDDEN').length
  const deleted = moderationCases.filter((item) => item.moderationStatus === 'DELETED').length
  const reported = moderationCases.filter((item) => (item.reportCount || 0) > 0).length

  return <>
    <PageTitle eyebrow="UGC POST MODERATION" title="블라인드·삭제 관리" description="회원·디렉터가 등록한 공개 콘텐츠는 즉시 노출하고, 문제 발생이나 작성자 삭제 이후에만 운영자가 사후 처리합니다." />
    <div className="policy-callout"><strong>사전 승인 없이 바로 공개합니다.</strong><span>운영자가 매번 검수하지 않습니다. 신고 또는 운영상 문제가 확인되면 블라인드하고, 작성자가 삭제한 콘텐츠는 DB에서 바로 지우지 않고 삭제됨 상태로 보존합니다.</span></div>
    <div className="metric-grid">
      <div className="metric-card metric-red"><span>블라인드</span><strong>{hidden}</strong><small>관리자 사후 비노출</small></div>
      <div className="metric-card metric-orange"><span>삭제됨</span><strong>{deleted}</strong><small>작성자 삭제 · DB 보존</small></div>
      <div className="metric-card metric-blue"><span>신고 있음</span><strong>{reported}</strong><small>필요 시 확인</small></div>
      <div className="metric-card metric-gold"><span>사후관리 유형</span><strong>5</strong><small>후기·포트폴리오·프로필·사진·FAQ</small></div>
    </div>
    <Card className="table-card">
      <div className="table-toolbar">
        <SearchField value={search} onChange={setSearch} placeholder="제목, 작성자, 유형, 대상 ID 검색" />
        <div className="filter-buttons">{FILTERS.map(([key, label]) => <button key={key} className={status === key ? 'active' : ''} onClick={() => setStatus(key)}>{label}</button>)}</div>
      </div>
      <div className="table-result-bar"><span>콘텐츠 <strong>{rows.length}</strong>건</span><small>블라인드·작성자 삭제·복구 이력을 확인합니다.</small></div>
      <div className="table-scroll"><table className="data-table"><thead><tr><th>유형</th><th>콘텐츠</th><th>작성자</th><th>신고</th><th>노출 상태</th><th>등록/삭제일</th></tr></thead><tbody>{rows.map((item) => <tr key={item.id} onClick={() => navigate(`/content/moderation/${item.id}`)}><td><strong>{MODERATION_TYPE_LABELS[item.targetType]}</strong><small>{item.source}</small></td><td><strong>{item.title}</strong><small>{item.targetId}</small></td><td>{item.owner}<small>{item.ownerType}</small></td><td>{item.reportCount ? `${item.reportCount}건` : '-'}</td><td><StatusBadge status={item.moderationStatus} /></td><td>{formatDate(item.deletedAt || item.submittedAt, true)}</td></tr>)}</tbody></table></div>
    </Card>
  </>
}

export function ModerationDetail({ id, navigate }) {
  const base = moderationCases.find((item) => item.id === id) || moderationCases[0]
  const [moderationStatus, setModerationStatus] = useState(base.moderationStatus)
  const [reason, setReason] = useState(base.reason || '')
  const [history, setHistory] = useState(base.history || [])
  const [message, setMessage] = useState('')
  const [hardDeleted, setHardDeleted] = useState(false)

  const blind = () => {
    if (!reason.trim()) {
      setMessage('블라인드 처리 사유를 입력해주세요.')
      return
    }
    setModerationStatus('HIDDEN')
    setHistory((current) => [...current, { label: '관리자 블라인드', detail: `${reason.trim()} · 김관리자`, at: '2026. 08. 11. 10:25' }])
    setMessage('블라인드 처리되었습니다. 사용자 화면에서는 즉시 비노출됩니다.')
  }

  const restore = () => {
    const label = moderationStatus === 'DELETED' ? '작성자 삭제 복구' : '블라인드 해제·복구'
    setModerationStatus('PUBLISHED')
    setHistory((current) => [...current, { label, detail: `${label} · 김관리자`, at: '2026. 08. 11. 10:25' }])
    setMessage(`${label} 처리되었습니다. 다시 사용자 화면에 노출됩니다.`)
  }

  const hardDelete = () => {
    const confirmed = window.confirm('DB에서 완전 삭제하면 복구할 수 없습니다. 이 콘텐츠를 완전 삭제하시겠습니까?')
    if (!confirmed) return
    setHardDeleted(true)
    setModerationStatus('PURGED')
    setHistory((current) => [...current, { label: '관리자 완전 삭제', detail: 'CONTENT_HARD_DELETE 권한으로 영구 삭제 · 김관리자', at: '2026. 08. 11. 10:25' }])
    setMessage('완전 삭제 처리되었습니다. 현재는 화면 검수용 Mock 동작이며 실제 DB 연결 시 원문/첨부 데이터는 복구할 수 없습니다.')
  }

  const canBlind = moderationStatus === 'PUBLISHED'
  const canRestore = ['HIDDEN', 'DELETED'].includes(moderationStatus)
  const canHardDelete = moderationStatus === 'DELETED'

  return <>
    <BackLink onClick={() => navigate('/content/moderation')} children="블라인드·삭제 관리로 돌아가기" />
    <PageTitle eyebrow="UGC POST MODERATION DETAIL" title={hardDeleted ? '완전 삭제된 콘텐츠' : base.title} description={`${MODERATION_TYPE_LABELS[base.targetType]} · ${base.owner} · ${base.targetId}`} actions={base.relatedRoute && !hardDeleted ? <button className="button soft" onClick={() => navigate(base.relatedRoute)}>원본 관리 화면 보기</button> : null} />
    {message && <div className="policy-callout"><strong>처리 안내</strong><span>{message}</span></div>}
    {moderationStatus === 'DELETED' && <div className="policy-callout"><strong>작성자가 삭제한 항목입니다.</strong><span>사용자 화면에서는 비노출되지만 DB 원문과 첨부는 보존되어 있습니다. 관리자가 복구하거나 완전 삭제할 수 있습니다.</span></div>}
    {hardDeleted && <div className="policy-callout"><strong>완전 삭제됨</strong><span>실제 운영에서는 콘텐츠 원문과 연결 첨부를 제거하고, 감사 로그에는 관리자·삭제 시각·대상 ID만 남깁니다.</span></div>}
    <SummaryGrid items={[
      { label: '노출 상태', value: <StatusBadge status={moderationStatus} /> },
      { label: '콘텐츠 유형', value: MODERATION_TYPE_LABELS[base.targetType] },
      { label: '작성자', value: base.owner },
      { label: '신고', value: base.reportCount ? `${base.reportCount}건` : '없음' },
    ]} />
    <div className="dashboard-grid half">
      <Card title="원문·첨부" description="삭제됨 상태에서도 관리자는 원문과 첨부를 확인할 수 있습니다.">
        {hardDeleted ? <div className="admin-empty-inline">완전 삭제되어 원문과 첨부를 더 이상 복구할 수 없습니다.</div> : <><div className="admin-long-text">{base.preview}</div>{base.attachments?.length ? <div className="admin-file-list" style={{ marginTop: 14 }}>{base.attachments.map((file) => <div key={file}><span>▧</span><strong>{file}</strong><button type="button">미리보기</button></div>)}</div> : <div className="admin-empty-inline">첨부 파일 없음</div>}</>}
      </Card>
      <Card title="대상 정보"><DetailGrid items={[
        { label: '관리 Case ID', value: base.id },
        { label: '대상 ID', value: base.targetId },
        { label: '출처', value: base.source },
        { label: '작성자 유형', value: base.ownerType },
        { label: '등록일', value: formatDate(base.submittedAt, true) },
        ...(base.deletedAt ? [{ label: '작성자 삭제일', value: formatDate(base.deletedAt, true) }, { label: '삭제 주체', value: `${base.deletedBy} (${base.deletedByType})` }] : []),
        { label: '현재 상태', value: hardDeleted ? 'DB 완전 삭제' : moderationStatus === 'HIDDEN' ? '관리자 블라인드 · 사용자 비노출' : moderationStatus === 'DELETED' ? '작성자 삭제 · DB 보존' : '사용자 화면 공개' },
      ]} /></Card>
    </div>
    {!hardDeleted && <Card title="사후 처리" description="사전 공개 승인은 사용하지 않습니다. 문제가 확인된 콘텐츠만 블라인드하고, 작성자가 삭제한 항목은 복구하거나 관리자 권한으로 완전 삭제합니다.">
      {canBlind && <label style={{ display: 'grid', gap: 8 }}><strong style={{ fontSize: 13 }}>블라인드 사유</strong><textarea className="admin-textarea" value={reason} onChange={(event) => { setReason(event.target.value); setMessage('') }} placeholder="예: 개인정보 노출, 부적절한 표현, 공개 권한 문제 등" /></label>}
      <div className="side-action-stack" style={{ marginTop: canBlind ? 14 : 0 }}>
        {canBlind && <button className="button danger-soft wide" onClick={blind}>블라인드 처리</button>}
        {moderationStatus === 'HIDDEN' && <button className="button primary wide" onClick={restore}>블라인드 해제·복구</button>}
        {moderationStatus === 'DELETED' && <button className="button primary wide" onClick={restore}>삭제 항목 복구</button>}
        {canHardDelete && <button className="button danger-soft wide" onClick={hardDelete}>DB에서 완전 삭제</button>}
      </div>
      <p className="help-copy">실제 API 연결 시 작성자 삭제는 soft delete(`deleted_at`)만 기록합니다. 완전 삭제는 별도 관리자 권한과 재확인을 거치고 감사 로그를 남깁니다. 결제·환불·정산 등 거래 원장은 완전 삭제 대상이 아닙니다.</p>
    </Card>}
    <Card title="사후 처리·삭제 이력"><Timeline items={history} /></Card>
  </>
}
